import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Container, Typography, Box, CircularProgress } from '@mui/material'
import { useStore } from '../store/useStore'
import { getSessionFiles } from '../utils/api'
import { ImageGrid } from '../components/ImageGrid'
import { ActionButtons } from '../components/ActionButtons'

interface SessionFile {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string;
  public_url?: string;
  created_at: string;
  updated_at: string;
  selected?: boolean;
  order?: number | null;
}

export const SessionView = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const { setSessionId, setFiles } = useStore()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchFiles = async () => {
      if (!sessionId) return
      try {
        setLoading(true)
        setError(null)
        setSessionId(sessionId)
        const files = await getSessionFiles(sessionId)
        
        // Transform the files to include selection and order properties
        const enhancedFiles = files.map((file: SessionFile) => ({
          ...file,
          selected: false,
          order: null,
        }))
        
        setFiles(enhancedFiles)
      } catch (error) {
        console.error('Error fetching files:', error)
        setError('Failed to load session files. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchFiles()
  }, [sessionId, setSessionId, setFiles])

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" color="navy" gutterBottom>
        Fowix - WhatsApp Images to PDF
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        Select images to include in your PDF. Click an image to select/deselect it.
      </Typography>
      <ImageGrid />
      <ActionButtons />
    </Container>
  )
} 